import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setModal } from 'redux/index';
import { getTransferRecipient, createTransfer } from 'services';
import { useCreateDispatches } from 'helpers/hooks';
import { PrimaryButton, SecondaryButton, TextField } from 'components';

const errorMessage = (e) =>
	e?.response?.data?.message || 'This request cannot be processed at this time.';

// Reuses the PasswordConfirm modal classes so it looks like the other admin dialogs.
function TransferChildModal({ child, onClose, onSent }) {
	const [email, setEmail] = useState('');
	const [recipient, setRecipient] = useState(null);
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	const run = async (fn) => {
		setBusy(true);
		setError('');
		try {
			await fn();
		} catch (e) {
			setError(errorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const lookUp = () =>
		run(async () => setRecipient(await getTransferRecipient(email.trim())));
	const send = () =>
		run(async () => {
			await createTransfer(child.id, email.trim());
			onSent(recipient.agencyName);
		});

	return (
		<div className='password-confirm-container'>
			<div className='password-confirm-card'>
				{recipient ? (
					<>
						<h3>
							Transfer {child.firstname} {child.lastname} to{' '}
							{recipient.agencyName} ({recipient.firstname}{' '}
							{recipient.lastname})?
						</h3>
						<p>
							The receiving agency must accept, then you must release the
							child. Both steps must be finished within 14 days.
						</p>
					</>
				) : (
					<>
						<h3>Transfer to another Ella agency</h3>
						<TextField
							label='Receiving agency admin or manager email'
							type='email'
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onKeyDown={(e) => e.keyCode === 13 && email.trim() && lookUp()}
							autoFocus
							style={{ width: '85%' }}
						/>
					</>
				)}
				<div className='password-confirm-error' role='alert'>
					{error || null}
				</div>
				<div className='password-confirm-button-container'>
					<SecondaryButton
						onClick={recipient ? () => setRecipient(null) : onClose}>
						{recipient ? 'Back' : 'Cancel'}
					</SecondaryButton>
					<PrimaryButton
						disabled={busy || !email.trim()}
						onClick={recipient ? send : lookUp}>
						{recipient ? 'Send transfer' : 'Continue'}
					</PrimaryButton>
				</div>
			</div>
		</div>
	);
}

export default function TransferChildButton({ child }) {
	const dispatch = useDispatch();
	const history = useHistory();
	const { setSnackbar } = useCreateDispatches();
	const close = () => dispatch(setModal(false));

	const onSent = (agencyName) => {
		close();
		setSnackbar({ variant: 'success', message: `Transfer request sent to ${agencyName}` });
		history.push('/lifebooks/manage/transfers');
	};

	return (
		<SecondaryButton
			onClick={() =>
				dispatch(
					setModal(
						<TransferChildModal child={child} onClose={close} onSent={onSent} />
					)
				)
			}>
			Transfer
		</SecondaryButton>
	);
}
